import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, IconButton, Badge, TextField, InputAdornment,
  Drawer, List, ListItem, ListItemIcon, ListItemText, Typography,
  useMediaQuery, useTheme, Button, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const FIG_LOGO = 'https://figk-12.com/static/media/fig_CROP.cb0d7cf4314c63ba05ee.png';

export default function StorefrontNavbar() {
  const navigate = useNavigate();
  const { storeSlug } = useParams();
  const { cartCount, toggleDrawer } = useCart();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchValue, setSearchValue] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  // Store context is optional - if we're on a store page, show store branding
  const isStoreContext = !!storeSlug;
  const prefix = isStoreContext ? `/store/${storeSlug}` : '';

  const navLinks = isStoreContext
    ? [
        { label: 'Home', path: prefix, icon: <HomeIcon /> },
        { label: 'Products', path: `${prefix}/products`, icon: <StorefrontIcon /> },
      ]
    : [
        { label: 'Home', path: '/', icon: <HomeIcon /> },
      ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
      setMobileOpen(false);
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          zIndex: 1200,
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          color: '#1a2332',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, py: 0.5, gap: 2 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {isStoreContext && (
              <Button
                component={Link}
                to="/"
                size="small"
                startIcon={<ArrowBackIcon />}
                sx={{ mr: 1, color: '#888', textTransform: 'none', fontWeight: 500, fontSize: '0.8rem' }}
              >
                Marketplace
              </Button>
            )}
            <Box
              component={Link}
              to={prefix || '/'}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              <Box component="img" src={FIG_LOGO} alt="FIG" sx={{ height: 38 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700, color: '#1a2332',
                  display: { xs: 'none', sm: 'block' }, fontSize: '1.1rem',
                }}
              >
                Marketplace
              </Typography>
            </Box>
          </Box>

          {/* Desktop Nav Links */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {navLinks.map(link => (
                <Button
                  key={link.path}
                  component={Link}
                  to={link.path}
                  sx={{
                    fontWeight: 500, color: '#555', textTransform: 'none',
                    '&:hover': { color: '#1ebec8', bgcolor: 'rgba(30,190,200,0.04)' },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Right actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && (
              <Box component="form" onSubmit={handleSearch}>
                <TextField
                  size="small"
                  placeholder="Search products..."
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  sx={{ width: 200 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" type="submit"><SearchIcon fontSize="small" /></IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            <IconButton onClick={toggleDrawer} sx={{ color: '#555' }}>
              <Badge badgeContent={cartCount} color="primary">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>

            {!isMobile && (
              <Button
                component={Link}
                to={isAuthenticated ? '/admin' : '/admin/login'}
                size="small"
                variant="outlined"
                startIcon={<AdminPanelSettingsIcon fontSize="small" />}
                sx={{
                  borderColor: '#ddd', color: '#777', textTransform: 'none',
                  fontWeight: 500, fontSize: '0.8rem',
                  '&:hover': { borderColor: '#1ebec8', color: '#1ebec8' },
                }}
              >
                {isAuthenticated ? 'Admin' : 'Login'}
              </Button>
            )}

            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ color: '#555' }}>
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 300, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src={FIG_LOGO} alt="FIG" sx={{ height: 28 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>FIG Marketplace</Typography>
            </Box>
            <IconButton onClick={() => setMobileOpen(false)}><CloseIcon /></IconButton>
          </Box>

          <Divider />

          <Box component="form" onSubmit={handleSearch} sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search products..."
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" type="submit"><SearchIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <List sx={{ flex: 1 }}>
            {isStoreContext && (
              <ListItem
                component={Link}
                to="/"
                onClick={() => setMobileOpen(false)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(30,190,200,0.04)' } }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: '#888' }}><ArrowBackIcon /></ListItemIcon>
                <ListItemText primary="Back to Marketplace" primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItem>
            )}
            {navLinks.map(link => (
              <ListItem
                key={link.path}
                component={Link}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(30,190,200,0.04)' } }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: '#888' }}>{link.icon}</ListItemIcon>
                <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItem>
            ))}
          </List>

          <Divider />

          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              component={Link}
              to={isAuthenticated ? '/admin' : '/admin/login'}
              onClick={() => setMobileOpen(false)}
              startIcon={<AdminPanelSettingsIcon />}
              sx={{
                borderColor: '#ddd', color: '#555', textTransform: 'none', fontWeight: 500,
                '&:hover': { borderColor: '#1ebec8', color: '#1ebec8' },
              }}
            >
              {isAuthenticated ? 'Admin Panel' : 'Admin Login'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
